import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LogOutIcon,
  MenuIcon,
  XIcon,
  WrenchIcon,
  PackageIcon,
  ChevronDownIcon,
  FormInputIcon,
  ExternalLink,
  KeyRound
} from 'lucide-react';

import {
  RiHome5Line as HomeIcon,
  RiDatabase2Line as DatabaseIcon,
  RiFileTextLine as FileTextIcon,
  RiFileTextLine as FolderOpenIcon,
  RiChatPollLine as MessageSquareIcon,
  RiHomeLine as LayoutIcon,
  RiChat4Line as UsersIcon,
  RiRadioButtonLine as PhoneIcon,
  RiStarLine as StarIcon,
  RiChatNewLine as TrendingUpIcon,
  RiBookOpenLine as BookOpenIcon,
  RiCalendarScheduleLine as CalendarIcon,
  RiKeyLine as KeyIcon,
  RiSettings3Line as SettingsIcon,
} from "react-icons/ri";

import { useTenantAuth } from '../contexts/TenantAuthContext';
import { AppointmentConfig, tenantApi } from '../lib/tenant-api';
import { useQuery } from '@tanstack/react-query';
import { useTenantType } from '../hooks/useTenantType';
import { isMenuVisible, isDisabledAccess, canAccess } from '../lib/permissions';
import { useNewFeatures } from '../hooks/useNewFeatures';
import { NewBadge } from './common';
import Modal from './Modal';
import SecuritySection from './settings/SecuritySection';
import WorkspaceToggle from './expert/WorkspaceToggle';
import { useWorkspace } from '../hooks/useWorkspace';
import { EXPERT_NAV, EXPERT_ROUTES, rememberWorkspaceRoute } from '../lib/workspace';
import { domainExpertApi, expertAgentApi, expertKeys } from '../lib/domain-expert-api';

interface LayoutProps {
  children: React.ReactNode;
}

const getVisibleNavigation = (userRole: string | undefined) => {
  const allNavigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Conversations', href: '/conversations', icon: UsersIcon },
    { name: 'API Keys', href: '/keys', icon: KeyIcon },
    { name: 'Settings', href: '/settings', icon: SettingsIcon },
  ];

  return allNavigation.filter(item => isMenuVisible(userRole, item.href));
};

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const toggleMenu = (id: string) =>
    setOpenMenuId(prev => (prev === id ? null : id));
  const location = useLocation();
  const { admin, tenant, logout } = useTenantAuth();
  const { tenantType } = useTenantType();
  const { workspace, hasClient, hasExpert, showToggle, switchTo } = useWorkspace();
  const isExpertWorkspace = workspace === 'expert';
  const isSuperAdmin = admin?.role?.toLowerCase() === 'super_admin';
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const { data: appointmentConfig } = useQuery<AppointmentConfig | null>({
    queryKey: ['appointmentConfig'],
    queryFn: tenantApi.getAppointmentConfig,
    retry: false,
    enabled: hasClient,
  });

  const { data: flagCounts } = useQuery({
    queryKey: expertKeys.flagCounts(),
    queryFn: expertAgentApi.getFlagCounts,
    enabled: hasExpert && isExpertWorkspace && canAccess(admin?.role, EXPERT_ROUTES.flaggedAnswers),
    refetchInterval: 60000,
  });

  const { data: expertLoginLink } = useQuery({
    queryKey: expertKeys.loginLink(),
    queryFn: domainExpertApi.getLoginLink,
    enabled: hasExpert && isExpertWorkspace,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    rememberWorkspaceRoute(tenant?.id, location.pathname + location.search);
  }, [tenant?.id, location.pathname, location.search]);

  const appointmentEnabled = appointmentConfig?.is_active || false;
  const navigation = getVisibleNavigation(admin?.role);
  const expertNavigation = EXPERT_NAV
    .filter(item => isMenuVisible(admin?.role, item.href))
    .map(item => ({
      ...item,
      badge: item.href === EXPERT_ROUTES.flaggedAnswers && flagCounts?.open ? flagCounts.open : undefined,
    }));
  const { hasNewForNav } = useNewFeatures();

  const knowledgeBaseSubItems = [
    { name: 'Documents', href: '/knowledge-base/documents', icon: FileTextIcon },
    tenantType === 'product'
      ? { name: 'Products', href: '/knowledge-base/products', icon: PackageIcon }
      : { name: 'Services', href: '/knowledge-base/services', icon: WrenchIcon },
    { name: 'Embeddings', href: '/knowledge-base/embeddings', icon: DatabaseIcon },
  ];

  const contentManagementSubItems = [
    { name: 'Assets', href: '/content-management/assets', icon: FolderOpenIcon },
    { name: 'Question Management', href: '/content-management/questions', icon: MessageSquareIcon },
    { name: 'CTAs', href: '/content-management/ctas', icon: PhoneIcon },
    { name: 'Reviews', href: '/content-management/reviews', icon: StarIcon },
    { name: 'Home Page Configurations', href: '/content-management/homepage', icon: LayoutIcon },
  ];

  const conversionActivitySubItems = appointmentEnabled
    ? [
      { name: 'Appointments', href: '/appointments', icon: CalendarIcon },
      { name: 'Forms', href: '/engagement-activity/forms', icon: FormInputIcon },
      { name: 'Leads', href: '/engagement-activity/leads', icon: UsersIcon },
      { name: 'High Intent Actions', href: '/engagement-activity/events', icon: TrendingUpIcon },
    ]
    : [
      { name: 'Forms', href: '/engagement-activity/forms', icon: FormInputIcon },
      { name: 'Leads', href: '/engagement-activity/leads', icon: UsersIcon },
      { name: 'High Intent Actions', href: '/engagement-activity/events', icon: TrendingUpIcon },
    ];

  const isKnowledgeBasePath = location.pathname.startsWith('/knowledge-base');
  const isContentManagementPath = location.pathname.startsWith('/content-management');
  const isConversionActivityPath = location.pathname.startsWith('/engagement-activity') || location.pathname === '/appointments';

  useEffect(() => {
    if (isKnowledgeBasePath) setOpenMenuId('knowledge-base');
    else if (isContentManagementPath) setOpenMenuId('content-management');
    else if (isConversionActivityPath) setOpenMenuId('engagement-activity');
  }, [isKnowledgeBasePath, isContentManagementPath, isConversionActivityPath]);

  const handleLogout = () => {
    logout();
  };

  const renderNavigationItem = (item: any, isMobile = false) => {
    const disabled = isDisabledAccess(admin?.role, item.href);
    const isActive =
      item.href === '/'
        ? location.pathname === '/' ||
        location.pathname.startsWith('/dashboard/')
        : location.pathname === item.href ||
        // The Home screen page has no sidebar entry; it belongs to Settings.
        (item.href === EXPERT_ROUTES.settings && location.pathname === EXPERT_ROUTES.homeScreen);

    if (disabled) {
      return (
        <span
          key={item.name}
          className="border-transparent text-gray-300 cursor-not-allowed group flex items-center px-4 py-5 text-sm font-medium border-l-[3px]"
          title="You don't have access to this section"
        >
          <item.icon className="text-gray-300 mr-3 h-5 w-5 flex-shrink-0" />
          {item.name}
        </span>
      );
    }

    return (
      <Link
        key={item.name}
        to={item.href}
        className={`${isActive
          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
          : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          } group flex items-center px-4 py-5 text-sm font-medium border-l-[3px]`}
        onClick={() => isMobile && setSidebarOpen(false)}
      >
        <item.icon
          className={`${isActive ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500'
            } mr-3 h-5 w-5 flex-shrink-0`}
        />
        {item.name}
        {hasNewForNav(item.href) && <NewBadge dot />}
        {item.badge ? (
          <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-600 px-1.5 py-0.5 text-xs font-semibold text-white">
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        ) : null}
      </Link>
    );
  };

  const renderSidebarFooterExtras = () => (
    <>
      {isExpertWorkspace && hasExpert && (expertLoginLink?.login_url || !isSuperAdmin) && (
        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3 space-y-1">
          {expertLoginLink?.login_url && (
            <a
              href={expertLoginLink.login_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 py-1.5 text-sm font-medium text-gray-600 hover:text-indigo-700"
            >
              <ExternalLink className="h-4 w-4 text-gray-400" />
              Open expert app
            </a>
          )}
          {!isSuperAdmin && (
            <button
              type="button"
              onClick={() => setChangePasswordOpen(true)}
              className="flex w-full items-center gap-2 py-1.5 text-sm font-medium text-gray-600 hover:text-indigo-700"
            >
              <KeyRound className="h-4 w-4 text-gray-400" />
              Change password
            </button>
          )}
        </div>
      )}
      {showToggle && (
        <div className="flex-shrink-0 border-t border-gray-200 p-4">
          <WorkspaceToggle value={workspace} onChange={switchTo} />
        </div>
      )}
    </>
  );

  const renderExpertNavigation = (isMobile: boolean) =>
    expertNavigation.map((item) => (
      <div key={item.name} className=" border-b border-gray-200">
        {renderNavigationItem(item, isMobile)}
      </div>
    ));

  const renderSubMenuItem = (subItem: any, isMobile: boolean) => {
    const disabled = isDisabledAccess(admin?.role, subItem.href);
    const isActive = location.pathname === subItem.href;

    if (disabled) {
      return (
        <span
          key={subItem.name}
          className="border-transparent text-gray-300 cursor-not-allowed group flex items-center pl-8 pr-4 py-2.5 text-sm font-medium border-l-[3px]"
          title="You don't have access to this section"
        >
          <subItem.icon className="text-gray-300 mr-3 h-[18px] w-[18px] flex-shrink-0" />
          {subItem.name}
        </span>
      );
    }

    return (
      <Link
        key={subItem.name}
        to={subItem.href}
        className={`${isActive
          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
          : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          } group flex items-center pl-8 pr-4 py-4 text-sm font-medium border-l-[3px]`}
        onClick={() => isMobile && setSidebarOpen(false)}
      >
        <subItem.icon
          className={`${isActive ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500'
            } mr-3 h-[18px] w-[18px] flex-shrink-0`}
        />
        {subItem.name}
        {hasNewForNav(subItem.href) && <NewBadge dot />}
      </Link>
    );
  };

  const renderKnowledgeBaseMenu = (isMobile = false) => {
    const visibleSubItems = knowledgeBaseSubItems.filter(item => isMenuVisible(admin?.role, item.href));
    if (visibleSubItems.length === 0) return null;
    const isOpen = openMenuId === 'knowledge-base';

    return (
      <div key="knowledge-base">
        <button
          onClick={() => toggleMenu('knowledge-base')}
          className="text-gray-600 hover:bg-gray-50 hover:text-gray-900 group flex items-center w-full px-4 py-5 text-sm font-medium"
        >
          <BookOpenIcon className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-left">Knowledge Base</span>
          <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <div
          className="grid transition-all duration-300 ease-in-out"
          style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <div className="space-y-1">
              {visibleSubItems.map((subItem) => renderSubMenuItem(subItem, isMobile))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContentManagementMenu = (isMobile = false) => {
    const visibleSubItems = contentManagementSubItems.filter(item => isMenuVisible(admin?.role, item.href));
    if (visibleSubItems.length === 0) return null;
    const isOpen = openMenuId === 'content-management';

    return (
      <div key="content-management">
        <button
          onClick={() => toggleMenu('content-management')}
          className="text-gray-600 hover:bg-gray-50 hover:text-gray-900 group flex items-center w-full px-4 py-5 text-sm font-medium"
        >
          <DatabaseIcon className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-left">Content Management</span>
          <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <div
          className="grid transition-all duration-300 ease-in-out"
          style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <div className="space-y-1">
              {visibleSubItems.map((subItem) => renderSubMenuItem(subItem, isMobile))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderConversionActivityMenu = (isMobile = false) => {
    const visibleSubItems = conversionActivitySubItems.filter(item => isMenuVisible(admin?.role, item.href));
    if (visibleSubItems.length === 0) return null;
    const isOpen = openMenuId === 'engagement-activity';

    return (
      <div key="engagement-activity">
        <button
          onClick={() => toggleMenu('engagement-activity')}
          className="text-gray-600 hover:bg-gray-50 hover:text-gray-900 group flex items-center w-full px-4 py-5 text-sm font-medium"
        >
          <TrendingUpIcon className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-left">Engagement Data</span>
          <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <div
          className="grid transition-all duration-300 ease-in-out"
          style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <div className="space-y-1">
              {visibleSubItems.map((subItem) => renderSubMenuItem(subItem, isMobile))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <h1 className="text-lg font-semibold text-gray-900">{tenant?.name}</h1>
            </div>
            <nav className="mt-5 px-2">
              {isExpertWorkspace ? renderExpertNavigation(true) : (<>
              <div className=" border-b border-gray-200">
                {navigation.slice(0, 1).map((item) => renderNavigationItem(item, true))}
              </div>
              <div className=" border-b border-gray-200">
                {renderContentManagementMenu(true)}
              </div>
              <div className=" border-b border-gray-200">
                {renderKnowledgeBaseMenu(true)}
              </div>
              <div className=" border-b border-gray-200">
                {navigation.slice(1, 2).map((item) => renderNavigationItem(item, true))}
              </div>
              <div className=" border-b border-gray-200">
                {renderConversionActivityMenu(true)}
              </div>
              {navigation.slice(2).map((item) => (
                <div key={item.name} className=" border-b border-gray-200">
                  {renderNavigationItem(item, true)}
                </div>
              ))}
              </>)}
            </nav>
          </div>
          {renderSidebarFooterExtras()}
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center w-full">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{admin?.email}</p>
                {/* @ts-ignore */}
                <p className="text-xs text-gray-500 capitalize">{admin?.role?.replaceAll('_', ' ')}</p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-3 inline-flex items-center p-2 border border-transparent shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                title="Logout"
              >
                <LogOutIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:flex md:w-80 md:flex-col md:fixed md:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <h1 className="text-2xl font-bold text-gray-900">{tenant?.name}</h1>
            </div>
            <nav className="mt-8 flex-1 bg-white">
              {isExpertWorkspace ? renderExpertNavigation(false) : (<>
              <div className=" border-b border-gray-200">
                {navigation.slice(0, 1).map((item) => renderNavigationItem(item, false))}
              </div>
              <div className=" border-b border-gray-200">
                {renderContentManagementMenu(false)}
              </div>
              <div className=" border-b border-gray-200">
                {renderKnowledgeBaseMenu(false)}
              </div>
              <div className=" border-b border-gray-200">
                {navigation.slice(1, 2).map((item) => renderNavigationItem(item, false))}
              </div>
              <div className=" border-b border-gray-200">
                {renderConversionActivityMenu(false)}
              </div>
              {navigation.slice(2).map((item) => (
                <div key={item.name} className=" border-b border-gray-200">
                  {renderNavigationItem(item, false)}
                </div>
              ))}
              </>)}
            </nav>
          </div>
          {renderSidebarFooterExtras()}
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center w-full">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{admin?.email}</p>
                {/* @ts-ignore */}
                <p className="text-xs text-gray-500 capitalize">{admin?.role?.replaceAll('_', ' ')}</p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-3 inline-flex items-center p-2 border border-transparent  shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                title="Logout"
              >
                <LogOutIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="md:pl-80 flex flex-col flex-1">
        <div className="sticky top-0 z-10 md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <span className="text-base font-semibold text-gray-900 truncate">{tenant?.name}</span>
          <button
            type="button"
            className="h-10 w-10 inline-flex items-center justify-center text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => setSidebarOpen(true)}
          >
            <MenuIcon className="h-6 w-6" />
          </button>
        </div>

        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>

      {changePasswordOpen && isExpertWorkspace && !isSuperAdmin && (
        <Modal isOpen onClose={() => setChangePasswordOpen(false)} title="Change password" size="sm">
          <SecuritySection />
        </Modal>
      )}
    </div>
  );
}
